// =============================================================
// FLOW (b): Location & device tree → Device Card.
//
// Reads the REAL location + device + task + spare data from /api/v1, builds the
// adjacency tree from location.parentId, and lists devices under each location.
// Clicking a device opens the 7-tab Device Card with that device's live tasks
// + spare rows (joined with stock levels for the booking math).
// =============================================================

import { useMemo, useState } from "react";
import type { Device, LocationNode, Spare, Task } from "@perpetuum/core";
import { useCollection, useDeviceTasks, useDevices, useLocations, useSpares, useSpareLevels } from "../lib/queries.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Icon, ICONS } from "../components/primitives.js";
import { DeviceCard, type CustomFieldDefLite, type DeviceSpareRow } from "./DeviceCard.js";

interface TreeNodeT {
  loc: LocationNode;
  children: TreeNodeT[];
  devices: Device[];
}

function buildTree(locations: LocationNode[], devices: Device[]): TreeNodeT[] {
  const byParent = new Map<string | undefined, LocationNode[]>();
  for (const l of locations) {
    const key = l.parentId ?? undefined;
    const arr = byParent.get(key) ?? [];
    arr.push(l);
    byParent.set(key, arr);
  }
  const devByLoc = new Map<string | undefined, Device[]>();
  for (const d of devices) {
    const key = d.locationId ?? undefined;
    const arr = devByLoc.get(key) ?? [];
    arr.push(d);
    devByLoc.set(key, arr);
  }
  const make = (loc: LocationNode): TreeNodeT => ({
    loc,
    children: (byParent.get(loc.id) ?? []).map(make),
    devices: devByLoc.get(loc.id) ?? [],
  });
  return (byParent.get(undefined) ?? []).map(make);
}

function DeviceRow({ device, depth, onOpen }: { device: Device; depth: number; onOpen: () => void }) {
  return (
    <div className="tree-node-row" style={{ paddingLeft: depth * 16 + 8 }} onClick={onOpen} role="button" tabIndex={0}>
      <span className="tree-twirl" />
      <Icon d={ICONS.wrench} size={14} />
      <span className="tree-name">{device.name}</span>
      <span className={`tree-led ${device.status ?? "operational"}`} title={device.status} />
    </div>
  );
}

function LocNode({
  node,
  depth,
  onOpenDevice,
}: {
  node: TreeNodeT;
  depth: number;
  onOpenDevice: (d: Device) => void;
}) {
  const [open, setOpen] = useState(true);
  const hasKids = node.children.length > 0 || node.devices.length > 0;
  return (
    <div>
      <div
        className="tree-node-row"
        style={{ paddingLeft: depth * 16 + 8 }}
        onClick={() => setOpen((o) => !o)}
        role="button"
        tabIndex={0}
      >
        <span className="tree-twirl">
          {hasKids && <Icon d={open ? ICONS.chevronDown : ICONS.chevron} size={14} />}
        </span>
        <Icon d={ICONS.tree} size={14} />
        <span className="tree-name">{node.loc.name}</span>
      </div>
      {open && (
        <div>
          {node.children.map((c) => (
            <LocNode key={c.loc.id} node={c} depth={depth + 1} onOpenDevice={onOpenDevice} />
          ))}
          {node.devices.map((d) => (
            <DeviceRow key={d.id} device={d} depth={depth + 1} onOpen={() => onOpenDevice(d)} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Loads the spare levels for the selected device's card (booking math). */
function DeviceCardLoader({
  device,
  crumb,
  tasks,
  spares,
  customFieldDefs,
  onClose,
}: {
  device: Device;
  crumb: string;
  tasks: Task[];
  spares: Spare[];
  customFieldDefs: CustomFieldDefLite[];
  onClose: () => void;
}) {
  // Representative: show all catalogue spares with their first stock-level row.
  // (Device↔spare mapping is via task_spares; this proves the booking-math read.)
  const first = spares[0];
  const levels = useSpareLevels(first?.id ?? null);

  const spareRows: DeviceSpareRow[] = spares.map((s) => {
    const lvl = s.id === first?.id ? (levels.data ?? [])[0] : undefined;
    return { spare: s, inStock: lvl?.inStock ?? 0, booked: lvl?.booked ?? 0 };
  });

  // Measurements, files, downtimes + history are read LIVE from the API inside
  // the Device Card (Phase 9.2) — no synthetic derivation here.
  return (
    <DeviceCard
      device={device}
      crumb={crumb}
      tasks={tasks}
      spareRows={spareRows}
      customFieldDefs={customFieldDefs}
      onClose={onClose}
    />
  );
}

export function Tree() {
  const t = useScopedT();
  const locations = useLocations();
  const devices = useDevices();
  const spares = useSpares();
  // Custom-field defs (key → label) so the Device Card shows the human label
  // (e.g. "Kritiskums") instead of the JSONB key (`cf_1`).
  const cfDefs = useCollection("custom-field-defs");
  const [selected, setSelected] = useState<Device | null>(null);
  // Fetch ONLY the selected device's work orders server-side (`?deviceId=`)
  // rather than loading the whole tenant's tasks and filtering client-side —
  // the latter does not scale past thousands of rows (Wave-2 audit fix).
  const deviceTasksQ = useDeviceTasks(selected?.id ?? null);

  const tree = useMemo(
    () => buildTree(locations.data ?? [], devices.data ?? []),
    [locations.data, devices.data],
  );

  const loading = locations.isLoading || devices.isLoading;
  const deviceTasks = selected ? (deviceTasksQ.data ?? []) : [];
  const crumb = selected
    ? [(locations.data ?? []).find((l) => l.id === selected.locationId)?.name, selected.name]
        .filter(Boolean)
        .join("  ›  ")
    : "";

  return (
    <div>
      <h2 className="d-h2">{t("tree.title")}</h2>
      <p className="d-sub">{t("tree.sub")}</p>

      <div className="d-card" style={{ padding: 14, maxWidth: 560 }}>
        {loading ? (
          <div className="ad-none">{t("common.loading")}</div>
        ) : tree.length === 0 && (devices.data ?? []).length === 0 ? (
          <div className="ad-none">{t("tree.empty")}</div>
        ) : (
          <div className="tree">
            {tree.map((n) => (
              <LocNode key={n.loc.id} node={n} depth={0} onOpenDevice={setSelected} />
            ))}
            {/* Devices with no location still need to be reachable. */}
            {(devices.data ?? [])
              .filter((d) => !d.locationId)
              .map((d) => (
                <DeviceRow key={d.id} device={d} depth={0} onOpen={() => setSelected(d)} />
              ))}
          </div>
        )}
      </div>

      {selected && (
        <DeviceCardLoader
          device={selected}
          crumb={crumb}
          tasks={deviceTasks}
          spares={spares.data ?? []}
          customFieldDefs={(cfDefs.data ?? []) as unknown as CustomFieldDefLite[]}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
